/**
 * Who an alert delivers to.
 *
 * Separate from `alertSpec` on purpose, mirroring the engine's own split:
 * `ComparisonSpec` carries "everything the engine needs and nothing it does
 * not — no recipients, no channel, no severity", because delivery and
 * evaluation are scored in isolation and should not be entangled here either.
 *
 * Recipients reuse the Report columns unchanged — `report_recipients` and
 * `report_email_to_self`.
 */

import { isValidReportEmail } from './steps/ReportStepOutput';

/**
 * Whether the alert has anywhere to send (AC8).
 *
 * "Email to self" counts. It is a real recipient — the author's own address —
 * so an alert with self-delivery on and no typed addresses is deliverable and
 * must not be blocked. The step blocks only when there is genuinely nobody:
 * self-delivery off *and* an empty list.
 *
 * `reportEmailToSelf` is treated as on unless explicitly false, matching how
 * the Report Output step reads the same field.
 */
export const hasAnyRecipient = (formData) => {
  const emailToSelf = formData?.reportEmailToSelf !== false;
  if (emailToSelf) return true;
  const recipients = formData?.reportRecipients ?? [];
  return recipients.some((email) => String(email || '').trim() !== '');
};

/**
 * The form data with a typed-but-not-added address folded in, when it is valid.
 *
 * "Email To Others" keeps what the author typed in `pendingRecipient` until
 * they click Add. Step 3's Next commits it (`handleNext`), so the four-step
 * path never lost one. Creating from the conversation does not pass through
 * Next: an author who went to "Change who it goes to or when", typed a
 * colleague's address and came back saved without them, and the summary
 * still said "to you". Raised in review of #212, measured through the wizard.
 *
 * Folding here, rather than at each call site, keeps the save, the check and
 * the summary agreeing about who the alert goes to. An invalid pending address
 * is left out; `pendingRecipientProblem` refuses it.
 */
export const withPendingRecipient = (formData) => {
  const pending = String(formData?.pendingRecipient || '').trim();
  if (!pending || !isValidReportEmail(pending)) return formData;
  const recipients = formData?.reportRecipients ?? [];
  return {
    ...formData,
    reportRecipients: recipients.includes(pending) ? recipients : [...recipients, pending],
    pendingRecipient: '',
  };
};

/**
 * Why a typed address blocks saving, or null. The same refusal step 3's
 * `canProceed` applies before Next, so the shortcut cannot save what the
 * four-step path would refuse.
 */
export const pendingRecipientProblem = (formData) => {
  const pending = String(formData?.pendingRecipient || '').trim();
  if (!pending || isValidReportEmail(pending)) return null;
  return `"${pending}" under Email To Others is not a valid address. Fix it or clear it before creating.`;
};

export default hasAnyRecipient;
